import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./ChatPromptContainer.css";

// components
import RichInputBox from "../RichInputBox/RichInputBox";
import DropdownSelector from "../DropdownSelector/DropdownSelector";
import GenericTooltip from "../GenericTooltip/GenericTooltip";
import SendStopButton from "../SendStopButton/SendStopButton";
import SavePromptModal from "../SavePromptModal/SavePromptModal";
import PromptLibrary from "../PromptLibrary/PromptLibrary";
import DeepInsightsPromptLibraryModal from "../DeepInsightsPromptLibraryModal/DeepInsightsPromptLibraryModal";
import DeepInsightsSourcePicker from "../DeepInsightsSourcePicker/DeepInsightsSourcePicker";

// store
import { useDispatch, useSelector } from "react-redux";
import { setWebSearchEnabled, setFileUploaded, setThinkingEnabled, setSelectedTool as setStoreSelectedTool, setTalentPulseWebSearchEnabled } from "../../store/actions";

// utility, hooks & constants
import { getFileTypeCategory } from "../../utils/fileHelper";
import { useConstants } from "../../constant/constantProvider";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import usePrompts from "../../hooks/usePrompts";
import useIsMobile from "../../hooks/useIsMobile";
import { KEYS } from "../../constant/keyConstants";
import useOutsideClick from "../../hooks/useOutsideClick";

// assets
import dropDownIcon from "../../assets/new-images/dropdown-icon.svg"
import plusIcon from "../../assets/new-images/plus-icon.svg";
import saveIcon from "../../assets/new-images/prompt-save-icon.svg";
import searchIcon from "../../assets/new-images/search-icon.svg";
import webSearchIcon from "../../assets/new-images/web-search-icon.svg";
import enableWebSearchIcon from "../../assets/new-images/enable-web-search-icon.svg";
import toolsIcon from "../../assets/new-images/tools-icon.svg";
import toolsActiveIcon from "../../assets/new-images/active-tools-icon.svg";
import thinkingIcon from "../../assets/new-images/thinking-icon.svg";
import enableThinkingIcon from "../../assets/new-images/active-thinking-icon.svg";
import enhanceIcon from "../../assets/new-images/enhance-icon.svg";
import fileUploadIcon from "../../assets/new-images/file-upload-icon.svg";
import blueCloseIcon from "../../assets/new-images/blue-close-icon.svg";
import greenCloseIcon from "../../assets/new-images/green-close-icon.svg";
import closeIcon from "../../assets/new-images/close-icon.svg";
import promptLibraryIcon from "../../assets/new-images/prompt-library-icon.svg";
import candidateIcon from "../../assets/new-images/candidate-icon.svg";
import voiceConversationIcon from "../../assets/new-images/voice-conversation-icon.svg";

// models icon
import gptIcon from "../../assets/icons/model-icons/chat-gpt-icon.svg";
import claudeIcon from "../../assets/icons/model-icons/sonnet-icon.svg";
import geminiIcon from "../../assets/icons/model-icons/gemini-icon.svg";
import grokIcon from "../../assets/icons/model-icons/grok-icon.svg";
import defaultModelIcon from "../../assets/icons/model-icons/auto-select-icon.svg";

// active models icon
import activeGptIcon from "../../assets/icons/model-icons/active-gpt-icon.svg";
import activeClaudeIcon from "../../assets/icons/model-icons/active-claude-icon.svg";
import activeGeminiIcon from "../../assets/icons/model-icons/active-gemini-icon.svg";
import activeGrokIcon from "../../assets/icons/model-icons/active-grok-icon.svg";
import activeDefaultModelIcon from "../../assets/icons/model-icons/active-auto-select-icon.svg";

// file icons
import wordIcon from "../../assets/icons/word.svg";
import pdfIcon from "../../assets/icons/pdf.svg";
import excelIcon from "../../assets/icons/microsoft-excel.svg";
import pptIcon from "../../assets/icons/microsoft-powerpoint.svg";
import textIcon from "../../assets/icons/txt.svg";
import xmlIcon from "../../assets/icons/xml.svg";
import htmlIcon from "../../assets/icons/html.svg";
import jsonIcon from "../../assets/icons/json.svg";
import csvIcon from "../../assets/icons/csv.svg";
import imageIcon from "../../assets/icons/image.svg";

const fileTypeIcons = {
  pdf: pdfIcon,
  word: wordIcon,
  doc: wordIcon,
  excel: excelIcon,
  ppt: pptIcon,
  text: textIcon,
  html: htmlIcon,
  xml: xmlIcon,
  json: jsonIcon,
  csv: csvIcon,
  image: imageIcon,
};

const ChatPromptContainer = forwardRef(({
  value = "",
  onChange,
  isLoading = false,
  isEnhancing = false,
  charLimit = 5000,
  placeholder = "Enter your prompt...",
  handleSendMessage,
  handleStopStreaming,
  showStopStreaming,
  models = [],
  selectedModel = "",
  onModelChange,
  isModelDisabled = false,
  showEnhance = false,
  showSavePrompt = false,
  showWebSearch = false,
  showTalentPulseWebSearch = false,
  showDocUpload = false,
  showImageGeneration = false,
  handleFileChange,
  handlePromptEnhancing,
  onSavePrompt,
  onDeletePrompt,
  selectedFiles = [],
  setSelectedFiles, // Passed for independent removal logic
  fileInputRef,
  richInputRef,
  // Suggestive prompts: [{ label: "Category", prompts: ["prompt1", "prompt2"] }]
  categories = [],
  activeCategory = null,
  onSuggestivePromptClick,
  showThinking = false,
  setIsTextareaFocused,
  hasMessages = false,
  onNewSession,
  showVoiceInput = false,
  theme = "default",
  toolItems = [],
  onVoiceStart,
  onVoiceStop,
  /** Called when a stop/pause keyword is detected during voice input. */
  onVoiceKeyword,
  /** When true, the mic stays visible and active during response generation. */
  keepListeningWhileLoading = false,
  sendLabel,
  contentPulseChip,
  selectedCandidateChip,
  profileTypeChip,
  isVoiceStopFeature = false,
  voiceWsUrl = null,
  footerText = "",
  disabled = false,
  deepInsightsSourcePicker = {},
  /** DeepInsights-only: opens the full-screen "tap to talk" conversation overlay. */
  onOpenVoiceConversation = null,
  /** Auto-send after this many ms of silence during voice input (e.g. 3000). */
  autoSendDelayMs = null,
  /** Called when silence auto-send fires. Should trigger handleSendMessage in parent. */
  onSilenceAutoSend = null,
}, ref) => {
  const constants = useConstants();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const isCompactView = window.innerWidth <= 1024;
  const { subscribe, emit } = useEventEmitter();

  const {
    WEB_CONTENT_TOGGLE_TOOLTIP,
    WEB_CONTENT_TOGGLE_DISABLED_TOOLTIP,
    WEB_CONTENT_TOGGLE_ENABLED_TOOLTIP,
    THINKING_NOT_SUPPORTED_TOOLTIP,
    THINKING_ENABLED_TOOLTIP,
    THINKING_DISABLED_TOOLTIP,
    CHAT_INFO_TEXT,
    ENHANCING_PROMPT,
    PLEASE_WAIT_GENERATING,
    START_TYPING_TO_REFINE,
    CLICK_TO_REFINE,
    PLEASE_WAIT,
    WEB_SEARCH_ENABLED_UPLOAD_DISABLED,
    UPLOAD_FILE,
    SAVE_PROMPT_TOOLTIP,
    SAVE_PROMPT_DISABLED_TOOLTIP,
    ENABLE_WEB_SEARCH,
    DISABLE_WEB_SEARCH,
    // eslint-disable-next-line
    PROMPT_LIBRARY,
  } = useConstants();

  const footerInfoText = footerText || CHAT_INFO_TEXT;

  const isDeepInsightsTheme = theme === "deep-insights";

  const selectedTool = useSelector((state) => state.state.selectedTool);
  const webSearchEnabled = useSelector((state) => state.state.webSearchEnabled);
  const talentPulseWebSearchEnabled = useSelector((state) => state.state.talentPulseWebSearchEnabled);
  const isFileUploaded = useSelector((state) => state.state.isFileUploaded);
  const thinkingEnabled = useSelector((state) => state.state.thinkingEnabled);

  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [saveModalPrefill, setSaveModalPrefill] = useState(null);

  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const [isContentPulseActive, setIsContentPulseActive] = useState(false);
  const [isSelectedCandidatesOpen, setIsSelectedCandidatesOpen] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);

  useEffect(() => {
    if (contentPulseChip?.isActive !== undefined) {
      setIsContentPulseActive(contentPulseChip.isActive);
    }
  }, [contentPulseChip?.isActive]);

  useEffect(() => {
    if (!selectedCandidateChip || !selectedCandidateChip.candidates || selectedCandidateChip.candidates.length === 0) {
      setIsSelectedCandidatesOpen(false);
    }
  }, [selectedCandidateChip]);
  const desktopPlusRef = useRef(null);

  const handleVoiceStartWrapper = useCallback(() => {
    setIsVoiceListening(true);
    if (onVoiceStart) onVoiceStart();
  }, [onVoiceStart]);

  const handleVoiceStopWrapper = useCallback(() => {
    setIsVoiceListening(false);
    if (onVoiceStop) onVoiceStop();
  }, [onVoiceStop]);

  const selectedCandidateMenuRef = useRef(null);

  useOutsideClick(desktopPlusRef, () => setIsPlusMenuOpen(false));
  useOutsideClick(selectedCandidateMenuRef, () => setIsSelectedCandidatesOpen(false));

  const sendStopBtnRef = useRef(null);

  const handleInputInteraction = useCallback(() => {
    sendStopBtnRef.current?.stopVoice?.();
  }, []);

  const handleTextChange = useCallback((val) => {
    onChange?.(val);
    if (val) {
      handleInputInteraction();
    }
  }, [onChange, handleInputInteraction]);

  useImperativeHandle(ref, () => ({
    startVoice: () => {
      sendStopBtnRef.current?.startVoice?.();
    },
    stopVoice: () => {
      sendStopBtnRef.current?.stopVoice?.();
    },
    /** Start background mic without sound/UI feedback (keyword-only mode). */
    startListening: () => {
      sendStopBtnRef.current?.startListening?.();
    },
    /** Stop background mic without sound/UI feedback. */
    stopListening: () => {
      sendStopBtnRef.current?.stopListening?.();
    },
  }));

  // Listen for save-prompt events emitted from user MessageBox bubbles
  useEffect(() => {
    const unsubscribe = subscribe("openSavePromptModal", (text) => {
      setSaveModalPrefill(text || "");
      setEditingPrompt(null);
      setIsSaveModalOpen(true);
    });
    const libraryUnsubscribe = subscribe("openPromptLibrary", () => {
      setIsPromptLibraryOpen(true);
    });
    const voiceInputUnsubscribe = subscribe("voiceInputStateChanged", (isActive) => {
      setIsVoiceListening(isActive);
    });

    return () => {
      unsubscribe();
      libraryUnsubscribe();
      voiceInputUnsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Fetch prompts globally to derive master lists for Dept/Task
  const { prompts: personalPrompts } = usePrompts(KEYS.PROMPT.PERSONAL, { enabled: isSaveModalOpen });
  const { prompts: suggestedPrompts } = usePrompts(KEYS.PROMPT.SUGGESTED, { enabled: isSaveModalOpen });

  const allPrompts = useMemo(() => [...personalPrompts, ...suggestedPrompts], [personalPrompts, suggestedPrompts]);

  const departmentOptions = useMemo(() => {
    const unique = new Set();
    allPrompts.forEach(p => {
      const dept = p.department || p.dept;
      if (dept) unique.add(String(dept).trim());
    });
    return Array.from(unique).sort().map(name => ({ name }));
  }, [allPrompts]);

  const taskOptions = useMemo(() => {
    const unique = new Set();
    allPrompts.forEach(p => {
      const task = p.task || p.task_name || p.taskName;
      if (task) unique.add(String(task).trim());
    });
    return Array.from(unique).sort().map(name => ({ name }));
  }, [allPrompts]);



  const activeModelName = selectedModel || models.find((m) => m.isSelected)?.name || models[0]?.name || "";
  const activeModel = models.find((m) => (m.name === activeModelName || m.label === activeModelName)) || models.find((m) => m.isSelected) || models[0];


  const isTextBoxDisabled = isLoading || isEnhancing || disabled;
  const isSendButtonDisabled = isLoading || isEnhancing || disabled || (!value.trim() && selectedFiles.length === 0);

  const trimmedValue = value?.trim() || "";
  const wordCount = trimmedValue.split(/\s+/).filter(Boolean).length;
  const charCount = trimmedValue.length;
  const isEnhanceEnabled = charCount >= 10 || wordCount >= 2;

  const currentModelName = (activeModelName || "").toLowerCase();
  const isThinkingModelSupported =
    currentModelName && !currentModelName.includes("grok");

  const handlePromptSelect = (prompt) => {
    if (onSuggestivePromptClick) onSuggestivePromptClick(prompt);
  };

  const handleInternalSave = (data) => {
    if (onSavePrompt) onSavePrompt(data);
  };

  const setSelectedTool = async (val) => {
    // If we have messages and the tool selection is changing (selecting, unselecting, or switching), start a new session.
    if (hasMessages && val !== selectedTool) {
      if (onNewSession) {
        await onNewSession(true); // true indicates we preserve the current input query
      }
    }
    if (val) {
      dispatch(setWebSearchEnabled(false));
      dispatch(setThinkingEnabled(false));
    }
    dispatch(setStoreSelectedTool(val));
  };

  const onWebSearchToggle = () => {
    if (!isFileUploaded) {
      dispatch(setWebSearchEnabled(!webSearchEnabled));
      richInputRef.current?.focus();
    }
  };

  const handleInternalKeyPress = (event) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        // Default behavior (new line) usually handled by RichInputBox
      } else {
        event.preventDefault();
        if (!isSendButtonDisabled && !isLoading) {
          handleSendMessage();
        }
      }
    }
  };

  const internalRemoveFile = (fileName) => {
    if (selectedFiles.length <= 1) {
      dispatch(setFileUploaded(false));
    }
    if (setSelectedFiles) {
      setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
    }
  };

  const hasCategories = categories.length > 0;

  const getModelIcon = (item, isActive = false) => {
    const name = (item?.name || item?.label || "").toLowerCase();
    if (name.includes("gpt")) return isActive ? activeGptIcon : gptIcon;
    if (name.includes("claude") || name.includes("sonnet")) return isActive ? activeClaudeIcon : claudeIcon;
    if (name.includes("gemini")) return isActive ? activeGeminiIcon : geminiIcon;
    if (name.includes("grok")) return isActive ? activeGrokIcon : grokIcon;
    return isActive ? activeDefaultModelIcon : defaultModelIcon;
  };

  return (
    <>
      <div className={`cpc-wrapper ${isDeepInsightsTheme ? 'deep-insights-cpc-theme' : ''} ${isVoiceListening ? 'cpc-listening-glow' : ''}`}>
        {selectedFiles?.length > 0 && (
          <div className="cpc-selected-files">
            {selectedFiles.map((file, idx) => {
              const fileType = getFileTypeCategory(file);
              const icon = fileTypeIcons[fileType] || wordIcon;
              const isImage = fileType === 'image';
              const base64Url = isImage && file.content ? `data:${file.type};base64,${file.content}` : null;

              return (
                <div key={idx} className={`cpc-file-tag ${isImage ? 'cpc-file-tag--image' : ''}`}>
                  {isImage && base64Url ? (
                    <img src={base64Url} alt="preview" className="cpc-file-icon cpc-file-preview" />
                  ) : (
                    <img src={icon} alt="file-icon" className="cpc-file-icon" />
                  )}
                  <span className="cpc-file-name" title={file.name}>{file.name}</span>
                  <button
                    type="button"
                    onClick={() => internalRemoveFile(file.name)}
                    className="button-style cpc-remove-file"
                    title="Remove file"
                  >
                    <img src={closeIcon} alt="close icon" />
                  </button>
                </div>
              );
            })}
          </div>
        )}


        {/* Mobile-only: active chips above input */}
        {isMobile && (() => {
          // Resolve selected tool — could be a top-level item or a sub-item
          const activeToolItem = selectedTool
            ? toolItems.find(t => t.id === selectedTool) ||
            toolItems.flatMap(t => t.subItems || []).find(s => s.value === selectedTool)
            : null;
          const activeChips = [
            activeToolItem && {
              key: "tool",
              icon: activeToolItem.activeImage || activeToolItem.defaultImage || activeToolItem.image,
              label: activeToolItem.label || activeToolItem.name,
              onRemove: () => setSelectedTool(null),
            },
            (showWebSearch && webSearchEnabled) && {
              key: "search",
              icon: enableWebSearchIcon,
              label: constants.WEB_SEARCH_LABEL,
              onRemove: () => dispatch(setWebSearchEnabled(false)),
            },
            (showThinking && thinkingEnabled && isThinkingModelSupported) && {
              key: "thinking",
              icon: enableThinkingIcon,
              label: constants.THINKING_LABEL,
              onRemove: () => dispatch(setThinkingEnabled(false)),
            },
          ].filter(Boolean);

          return activeChips.length > 0 ? (
            <div className="cpc-mobile-chip-row">
              {activeChips.map((chip) => (
                <div key={chip.key} className="cpc-mobile-chip cpc-mobile-chip--blue">
                  <img src={chip.icon} alt={chip.label} className="cpc-mobile-chip-icon" />
                  <span className="cpc-mobile-chip-label">{chip.label}</span>
                  <button
                    type="button"
                    className={`cpc-mobile-chip-close ${isLoading ? 'disabled' : ''}`}
                    onClick={() => !isLoading && chip.onRemove()}
                    disabled={isLoading}
                  >
                    <img src={blueCloseIcon} alt="close" />
                  </button>
                </div>
              ))}
            </div>
          ) : null;
        })()}

        <div className="cpc-input-area" onClickCapture={handleInputInteraction}>
          {isContentPulseActive && contentPulseChip && (
            <div className="cpc-content-pulse-meta-chip">
              <div className="cpc-mobile-chip cpc-mobile-chip--blue cpc-content-pulse-chip-inner">
                <img src={candidateIcon} alt="icon" className="cpc-mobile-chip-icon" />
                <span className="cpc-mobile-chip-label">{contentPulseChip.context || contentPulseChip.heading}</span>
                <button
                  type="button"
                  className="cpc-mobile-chip-close"
                  onClick={() => {
                    setIsContentPulseActive(false);
                    if (contentPulseChip.onRemove) contentPulseChip.onRemove();
                  }}
                >
                  <img src={blueCloseIcon} alt="close" />
                </button>
              </div>
              {selectedCandidateChip && (
                <div className="cpc-selected-candidates-chip" ref={selectedCandidateMenuRef}>
                  <span className="cpc-selected-candidates-icon" aria-hidden="true" />
                  <button
                    type="button"
                    className="cpc-selected-candidates-label"
                    onClick={() => setIsSelectedCandidatesOpen((isOpen) => !isOpen)}
                    aria-expanded={isSelectedCandidatesOpen}
                  >
                    {selectedCandidateChip.label}
                  </button>
                  <button
                    type="button"
                    className="cpc-selected-candidates-toggle"
                    onClick={() => setIsSelectedCandidatesOpen((isOpen) => !isOpen)}
                    aria-label="Show selected candidates"
                  >
                    <img
                      src={dropDownIcon}
                      alt=""
                      className={isSelectedCandidatesOpen ? 'is-open' : ''}
                    />
                  </button>
                  <button
                    type="button"
                    className="cpc-selected-candidates-close"
                    onClick={() => {
                      setIsSelectedCandidatesOpen(false);
                      selectedCandidateChip.onRemove();
                    }}
                    aria-label="Clear selected candidates"
                    disabled={isLoading}
                  >
                    <img src={greenCloseIcon} alt="" />
                  </button>
                  {isSelectedCandidatesOpen && selectedCandidateChip.candidates?.length > 0 && (
                    <div className="cpc-selected-candidates-menu">
                      <div className="cpc-selected-candidates-menu-title">
                        {constants.SELECTED_PROFILES_LABEL || "Selected profiles"} ({selectedCandidateChip.candidates.length})
                      </div>
                      <div className="cpc-selected-candidates-menu-list">
                        {selectedCandidateChip.candidates.map((candidate) => (
                          <div className="cpc-selected-candidates-menu-item" key={candidate.id}>
                            <span className="cpc-selected-candidates-avatar">{candidate.initials}</span>
                            <span className="cpc-selected-candidates-name">{candidate.name}</span>
                            <span className="cpc-selected-candidates-badge">{constants.CANDIDATE_BADGE_LABEL || "Candidate"}</span>
                            <button
                              type="button"
                              onClick={() => selectedCandidateChip.onRemoveCandidate?.(candidate.id)}
                              aria-label={`Remove ${candidate.name}`}
                              disabled={isLoading}
                            >
                              <img src={blueCloseIcon} alt="crossIcon" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {profileTypeChip && (
                <div className="cpc-profile-type-chip">
                  <span>{profileTypeChip.label}</span>
                  <button
                    type="button"
                    className="cpc-profile-type-close"
                    onClick={profileTypeChip.onRemove}
                    aria-label={`Remove ${profileTypeChip.label} filter`}
                    disabled={isLoading}
                  >
                    <img src={closeIcon} alt="" />
                  </button>
                </div>
              )}
            </div>
          )}
          <RichInputBox
            ref={richInputRef}
            value={value}
            onChange={handleTextChange}
            isTextBoxDisabled={isTextBoxDisabled}
            isLoading={isLoading}
            handleKeyPress={handleInternalKeyPress}
            setIsTextareaFocused={(val) => {
              if (setIsTextareaFocused) setIsTextareaFocused(val);
              if (val) handleInputInteraction();
            }}
            charLimit={charLimit}
            placeholder={placeholder}
            isEnhancing={isEnhancing}
          />
        </div>

        {/* ── Suggestive Prompts Panel removed from here ─────────────────────────── */}

        {/* ── Toolbar ────────────────────────────────────────────── */}
        <div className="cpc-toolbar">

          {/* Left: action icons */}
          <div className="cpc-toolbar-left">
            {isDeepInsightsTheme && (
              <button type="button" className="deep-insights-prompt-library-btn" onClick={() => setIsPromptLibraryOpen(true)}>
                <img src={promptLibraryIcon} alt={PROMPT_LIBRARY} />
                {PROMPT_LIBRARY}
              </button>
            )}

            {isDeepInsightsTheme && (
              <DeepInsightsSourcePicker
                {...deepInsightsSourcePicker}
                onManageSources={() => emit("openDeepInsightsConnectors")}
                disabled={isLoading}
              />
            )}

            {/* Plus Button & Flyout Card (Mobile & Desktop) */}
            {contentPulseChip && (
              <div ref={desktopPlusRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className={`cpc-icon-btn cpc-plus-btn ${isPlusMenuOpen ? 'active' : ''}`}
                  disabled={isLoading}
                  onClick={() => setIsPlusMenuOpen(prev => !prev)}
                >
                  <img src={plusIcon} alt="plus" />
                </button>

                {isPlusMenuOpen && contentPulseChip && (
                  <div className={`cpc-context-chip${isMobile ? ' cpc-context-chip--mobile' : ''}`}
                    onClick={() => {
                      if (isContentPulseActive) {
                        setIsContentPulseActive(false);
                        if (contentPulseChip.onRemove) contentPulseChip.onRemove();
                      } else {
                        setIsContentPulseActive(true);
                        if (contentPulseChip.onSelect) contentPulseChip.onSelect();
                      }
                      setIsPlusMenuOpen(false);
                    }}>
                    <div className="cpc-context-chip-heading">
                      {contentPulseChip.heading}
                    </div>
                    <div className={`cpc-context-chip-body ${isContentPulseActive ? "is-active" : ""}`}>
                      <div className="cpc-context-chip-icon-wrapper">
                        <img src={candidateIcon} alt="context" />
                      </div>
                      <span>{contentPulseChip.context}</span>
                      {isContentPulseActive && <img src={blueCloseIcon} alt="selected" className="cpc-context-chip-check-icon" />}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Desktop: Tools dropdown ─────────────────────────── */}
            {showImageGeneration && !isMobile && (
              <div className="cpc-tools-dropdown-wrapper">
                <DropdownSelector
                  items={toolItems}
                  type="selected-cross"
                  selected={selectedTool}
                  onChange={(val) => setSelectedTool(val)}
                  placeholder={{
                    name: constants.TOOLS,
                    image: selectedTool ? toolsActiveIcon : toolsIcon
                  }}
                  itemConfig={{
                    getLabel: (item) => item.name,
                    getValue: (item) => item.id,
                    getDefaultImage: (item) => item.image,
                    getActiveImage: (item) => item.activeImage || item.image,
                    getSubItems: (item) => item.subItems || null,
                  }}
                  direction="up"
                  theme="blue"
                  isDisabled={isLoading}
                />
              </div>
            )}

            {/* Enhance — visible on both mobile & desktop */}
            {showEnhance && (
              <GenericTooltip
                tooltipText={
                  isEnhancing
                    ? ENHANCING_PROMPT
                    : isLoading
                      ? PLEASE_WAIT_GENERATING
                      : !isEnhanceEnabled
                        ? START_TYPING_TO_REFINE
                        : CLICK_TO_REFINE
                }
                tooltipPosition="top"
              >
                <button
                  type="button"
                  className={`cpc-icon-btn cpc-prompt-enhancement-container ${(!value?.trim() || isLoading || isEnhancing || !isEnhanceEnabled) ? "disabled-button" : ""}`}
                  onClick={async () => {
                    await handlePromptEnhancing();
                    richInputRef.current?.focus();
                  }}
                  disabled={!value?.trim() || isLoading || isEnhancing || !isEnhanceEnabled}
                >
                  <img src={enhanceIcon} alt="enhance" />
                </button>
              </GenericTooltip>
            )}

            {showTalentPulseWebSearch && (
              <GenericTooltip
                tooltipText={talentPulseWebSearchEnabled ? DISABLE_WEB_SEARCH : ENABLE_WEB_SEARCH}
                tooltipPosition="top"
              >
                <button
                  type="button"
                  className={`cpc-icon-btn cpc-web-search-container ${talentPulseWebSearchEnabled ? "cpc-globe-btn--active" : ""}`}
                  onClick={() => {
                    dispatch(setTalentPulseWebSearchEnabled(!talentPulseWebSearchEnabled));
                    richInputRef.current?.focus();
                  }}
                >
                  <img src={talentPulseWebSearchEnabled ? enableWebSearchIcon : webSearchIcon} alt="talent pulse web search" />
                </button>
              </GenericTooltip>
            )}

            {/* Mobile: model selector in toolbar-left (after enhance) */}
            {isMobile && models.length > 0 && (
              <div className="cpc-model-selector-wrapper">
                <DropdownSelector
                  items={models}
                  type="selected"
                  selected={activeModelName}
                  onChange={onModelChange}
                  placeholder={{
                    name: activeModel?.name || activeModelName,
                    image: getModelIcon(activeModel)
                  }}
                  itemConfig={{
                    getLabel: (item) => item?.name || item?.label,
                    getValue: (item) => item?.name,
                    getDefaultImage: (item) => getModelIcon(item),
                    getActiveImage: (item) => getModelIcon(item, true),
                    getDisabled: (item) => item.name?.toLowerCase().includes("grok") && thinkingEnabled,
                  }}
                  showActiveIconInTrigger={false}
                  direction="up"
                  theme="default"
                  isDisabled={isModelDisabled}
                />
              </div>
            )}

            {/* ── Desktop-only: individual toolbar buttons ────────── */}
            {/* File upload */}
            {!isMobile && showDocUpload && (
              <GenericTooltip
                tooltipText={
                  isLoading
                    ? PLEASE_WAIT
                    : (showWebSearch && webSearchEnabled)
                      ? WEB_SEARCH_ENABLED_UPLOAD_DISABLED
                      : UPLOAD_FILE
                }
                tooltipPosition="top"
              >
                <label
                  className={`cpc-icon-btn cpc-doc-upload-container ${isLoading || (showWebSearch && webSearchEnabled) ? "disabled-button" : ""}`}
                >
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      handleFileChange(e);
                      richInputRef.current?.focus();
                    }}
                    className="cpc-file-input"
                    ref={fileInputRef}
                    disabled={isLoading || (showWebSearch && webSearchEnabled)}
                  />
                  <img src={fileUploadIcon} alt="upload" />
                </label>
              </GenericTooltip>
            )}

            {!isMobile && showWebSearch && selectedTool !== "IMAGE_GENERATION" && (
              <GenericTooltip
                tooltipText={isFileUploaded ? WEB_CONTENT_TOGGLE_DISABLED_TOOLTIP : webSearchEnabled ? WEB_CONTENT_TOGGLE_ENABLED_TOOLTIP : WEB_CONTENT_TOGGLE_TOOLTIP}
                tooltipPosition="top"
              >
                <button
                  type="button"
                  className={`cpc-icon-btn cpc-web-search-container ${webSearchEnabled ? "cpc-globe-btn--active" : ""} ${isFileUploaded ? "disabled-button" : ""}`}
                  onClick={onWebSearchToggle}
                  disabled={isFileUploaded}
                >
                  <img src={webSearchEnabled ? enableWebSearchIcon : webSearchIcon} alt="web search" />
                </button>
              </GenericTooltip>
            )}

            {!isMobile && showThinking && (
              <GenericTooltip
                tooltipText={
                  !isThinkingModelSupported
                    ? THINKING_NOT_SUPPORTED_TOOLTIP
                    : thinkingEnabled
                      ? THINKING_ENABLED_TOOLTIP
                      : THINKING_DISABLED_TOOLTIP
                }
                tooltipPosition="top"
              >
                <button
                  type="button"
                  className={`cpc-icon-btn cpc-thinking-container ${thinkingEnabled && isThinkingModelSupported ? "cpc-thinking-btn--active" : ""} ${!isThinkingModelSupported ? "disabled-button" : ""}`}
                  onClick={() => {
                    if (isThinkingModelSupported) {
                      dispatch(setThinkingEnabled(!thinkingEnabled));
                      richInputRef.current?.focus();
                    }
                  }}
                  disabled={!isThinkingModelSupported}
                  aria-label="Toggle thinking"
                >
                  <img
                    src={thinkingEnabled && isThinkingModelSupported ? enableThinkingIcon : thinkingIcon}
                    alt="thinking"
                  />
                </button>
              </GenericTooltip>
            )}

            {!isMobile && showSavePrompt && (
              <GenericTooltip
                tooltipText={
                  !value?.trim()
                    ? SAVE_PROMPT_DISABLED_TOOLTIP
                    : SAVE_PROMPT_TOOLTIP
                }
                tooltipPosition="top"
              >
                <button
                  type="button"
                  className={`cpc-icon-btn ${!value?.trim() ? 'disabled-button' : ''}`}
                  onClick={() => setIsSaveModalOpen(true)}
                  disabled={!value?.trim()}
                >
                  <img src={saveIcon} alt="Save" />
                </button>
              </GenericTooltip>
            )}
          </div>
          <div className="cpc-toolbar-right">
            {!isMobile && models.length > 0 && (
              <div className="cpc-model-selector-wrapper">
                <DropdownSelector
                  items={models}
                  type="selected"
                  selected={activeModelName}
                  onChange={onModelChange}
                  placeholder={{
                    name: activeModel?.name || activeModelName,
                    image: getModelIcon(activeModel)
                  }}
                  itemConfig={{
                    getLabel: (item) => item?.name || item?.label,
                    getValue: (item) => item?.name,
                    getDefaultImage: (item) => getModelIcon(item),
                    getActiveImage: (item) => getModelIcon(item, true),
                    getDisabled: (item) => item.name?.toLowerCase().includes("grok") && thinkingEnabled,
                  }}
                  showActiveIconInTrigger={false}
                  direction="up"
                  theme="default"
                  isDisabled={isModelDisabled}
                />
              </div>
            )}

            {/* DeepInsights-only: open full-screen "tap to talk" overlay.
                Guarded by both theme + prop so other apps/screens never render this button. */}
            {isDeepInsightsTheme && typeof onOpenVoiceConversation === "function" && (
              <GenericTooltip tooltipText="Tap to talk" tooltipPosition="top">
                <button
                  type="button"
                  className="cpc-icon-btn cpc-voice-conv-btn"
                  onClick={onOpenVoiceConversation}
                  disabled={disabled}
                  aria-label="Start voice conversation"
                >
                  <img src={voiceConversationIcon} alt="" aria-hidden="true" />
                </button>
              </GenericTooltip>
            )}

            <SendStopButton
              ref={sendStopBtnRef}
              isLoading={isLoading}
              isSendButtonDisabled={isSendButtonDisabled}
              handleSendMessage={handleSendMessage}
              handleStopStreaming={handleStopStreaming}
              showStopStreaming={showStopStreaming}
              sendLabel={!isCompactView ? (sendLabel || constants.GENERATE) : null}
              showVoiceInput={showVoiceInput}
              wsUrl={voiceWsUrl}
              hasText={!!value?.trim()}
              onTranscript={onChange}
              theme={theme}
              onVoiceStart={handleVoiceStartWrapper}
              onVoiceStop={handleVoiceStopWrapper}
              onKeyword={onVoiceKeyword}
              keepListeningWhileLoading={keepListeningWhileLoading}
              isVoiceStopFeature={isVoiceStopFeature}
              autoSendDelayMs={autoSendDelayMs}
              onSilenceAutoSend={onSilenceAutoSend}
            />
          </div>
        </div>

        {/* Inline prompt list — desktop only; mobile uses Dashboard */}
        {!isMobile && hasCategories && activeCategory && (
          <div className="cpc-inline-prompt-list">
            {categories
              .find((c) => c.label === activeCategory)
              ?.prompts.map((prompt, i) => (
                <button
                  key={i}
                  type="button"
                  className="cpc-prompt-item"
                  onClick={() => handlePromptSelect(prompt)}
                >
                  <span className="cpc-prompt-icon">
                    <img src={searchIcon} alt="search" />
                  </span>
                  {prompt}
                </button>
              ))}
          </div>
        )}
      </div>

      {footerInfoText && (
        <div className="cpc-info-text">{footerInfoText}</div>
      )}

      {isDeepInsightsTheme ? (
        <DeepInsightsPromptLibraryModal
          open={isPromptLibraryOpen}
          onClose={() => setIsPromptLibraryOpen(false)}
          onSelect={(p) => {
            onChange(p);
            setIsPromptLibraryOpen(false);
          }}
        />
      ) : (
        <PromptLibrary
          open={isPromptLibraryOpen}
          onClose={() => setIsPromptLibraryOpen(false)}
          onSelect={(p) => {
            onChange(p);
            setIsPromptLibraryOpen(false);
          }}
          onEdit={(prompt) => {
            setEditingPrompt(prompt);
            setIsSaveModalOpen(true);
          }}
          onDelete={onDeletePrompt}
        />
      )}

      {isSaveModalOpen && (
        <SavePromptModal
          open={isSaveModalOpen}
          onClose={() => {
            setIsSaveModalOpen(false);
            setEditingPrompt(null);
            setSaveModalPrefill(null);
          }}
          onSave={handleInternalSave}
          promptContent={saveModalPrefill !== null ? saveModalPrefill : value}
          initialData={editingPrompt}
          departmentOptions={departmentOptions}
          taskOptions={taskOptions}
        />
      )}
    </>
  );
});

export default React.memo(ChatPromptContainer);
